/**
 * Datagrid component that displays data in a grid format with interactive features.
 * This component handles the first user interactions for different actions like
 * viewing, filtering, searching, and deleting datasets.
 */

import React, { useMemo, useCallback } from "react";
import DataGrid, {
  Column,
  Grouping,
  Export,
  GroupPanel,
  FilterRow,
  SearchPanel,
  Scrolling,
  Editing,
  LoadPanel,
} from "devextreme-react/data-grid";
import ProgressBarCell from "../../../../common/pyramid/ProgressBarCell";
import { horusApi } from "../../../../../services/api/horus";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import saveAs from "file-saver";
import { formatUTCtoLocal } from "components/common/pyramid/FormatUTCtoLocal";

/**
 * Datagrid component for displaying and interacting with dataset information.
 * Provides functionality for filtering, grouping, exporting, and managing datasets.
 *
 * @param {Object} props - Component properties
 * @param {Array} props.users - List of users for lookup in the grid
 * @param {Array} props.groups - List of groups/companies for lookup in the grid
 * @param {Array} props.datasets - The datasets to display in the grid
 * @param {Function} props.onAddButtonClick - Callback function when the add button is clicked
 * @param {Function} props.onRefresh - Callback function when the refresh button is clicked
 * @param {Function} props.onRowClick - Callback function when a row is clicked
 * @param {boolean} props.isLoading - Loading state for data overlay
 */
const Datagrid = ({
  userList,
  companyList,
  datasets,
  onAddButtonClick,
  onRefresh,
  onDownload,
  onRowClick,
  isLoading,
}) => {
  const api = horusApi();

  const allowDeletion = useCallback(
    (e) => {
      return e.row.data.groupId === companyList[0]?.id;
    },
    [companyList]
  );

  const onExporting = useCallback((e) => {
    e.component.beginUpdate();
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Datasets");

    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      autoFilterEnabled: true,
    })
      .then(function () {
        workbook.xlsx.writeBuffer().then(function (buffer) {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            "Datasets.xlsx"
          );
        });
      })
      .then(function () {
        e.component.endUpdate();
      });
  }, []);

  const toolbarItems = useMemo(
    () => [
      {
        widget: "dxButton",
        options: {
          icon: "info",
          hint: "User Manual for the Data Table",
          onClick: onDownload,
        },
        location: "after",
      },
      {
        widget: "dxButton",
        options: {
          icon: "refresh",
          onClick: onRefresh,
        },
        location: "after",
      },
      {
        widget: "dxButton",
        options: {
          icon: "add",
          onClick: onAddButtonClick,
        },
        location: "after",
      },
    ],
    [onAddButtonClick, onRefresh, onDownload]
  );

  const onToolbarPreparing = (e) => {
    e.toolbarOptions.items.unshift(...toolbarItems);
  };

  const handleDelete = useCallback(
    async (e) => {
      try {
        await api.deleteDataset(e.key);
        window.dispatchEvent(new CustomEvent("refreshDatasets"));
      } catch (error) {
        console.error("Error deleting dataset:", error);
      }
    },
    [api]
  );

  const columns = useMemo(
    () => [
      {
        dataField: "groupId",
        caption: "Created by company",
        groupIndex: 0,
        lookup: {
          dataSource: companyList,
          valueExpr: "id",
          displayExpr: "displayName",
        },
        groupCellTemplate: (element, data) => element.append(data.text),
      },
      {
        dataField: "userId",
        caption: "Created by user",
        groupIndex: 1,
        lookup: {
          dataSource: userList,
          valueExpr: "id",
          displayExpr: "displayName",
        },
        groupCellTemplate: (element, data) => element.append(data.text),
      },
      {
        dataField: "description",
        caption: "Description",
      },
      {
        dataField: "evaluationDate",
        caption: "Evaluation Date",
        dataType: "date",
        format: "yyyy-MM-dd",
      },
      {
        dataField: "createdAt",
        caption: "Creation Date",
        dataType: "date",
        format: "yyyy-MM-dd",
        cellRender: (cellData) => {
          return formatUTCtoLocal(cellData.value);
        },
        calculateCellValue: (data) => {
          if (!data.createdAt) return null;
          const localDate = formatUTCtoLocal(data.createdAt);
          return typeof localDate === "string"
            ? new Date(localDate)
            : localDate;
        },
      },
      {
        dataField: "step",
        caption: "Step",
        alignment: "center",
        cellRender: (cellData) => {
          let errorMessage = cellData.data.errorMessage;

          if (errorMessage) {
            // Find the index of "IReader state:"
            const readerStateIndex = errorMessage.indexOf("IReader state:");

            // If "IReader state:" is found, keep only the text before it
            if (readerStateIndex !== -1) {
              errorMessage = errorMessage.substring(0, readerStateIndex).trim();
            }
          }

          return (
            <ProgressBarCell
              value={cellData.value}
              errorMessage={errorMessage}
            />
          );
        },
      },
    ],
    [companyList, userList, formatUTCtoLocal]
  );

  const gridOptions = useMemo(
    () => ({
      dataSource: datasets,
      keyExpr: "id",
      rowAlternationEnabled: false,
      showBorders: false,
      showColumnLines: false,
      hoverStateEnabled: true,
      allowColumnResizing: true,
      width: "100%",
      height: "60vh",
    }),
    [datasets]
  );

  return (
    <div style={{ width: "100%" }}>
      <div className="position-relative">
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        <DataGrid
          {...gridOptions}
          onToolbarPreparing={onToolbarPreparing}
          onExporting={onExporting}
          onCellPrepared={(e) => {
            if (e.rowType === "data") {
              e.cellElement.style.setProperty("cursor", "pointer", "important");
            }
          }}
          onRowClick={(e) => e.rowType === "data" && onRowClick(e.data)}
          onRowRemoving={handleDelete}
        >
          {/* Grid features configuration */}
          <FilterRow visible={true} applyFilter="auto" />
          <Scrolling mode="virtual" />
          <GroupPanel visible={true} allowColumnDragging={false} />
          <SearchPanel visible={true} highlightCaseSensitive={true} />
          <Grouping autoExpandAll={true} />
          <Export enabled={true} />
          <Editing
            mode="row"
            allowDeleting={allowDeletion}
            useIcons={true}
            texts={{
              confirmDeleteTitle: "Delete confirmation",
              confirmDeleteMessage:
                "Are you sure you want to delete your input ? If there are computation(s) related to this input, they will also be removed",
            }}
          />
          <LoadPanel enabled={false} showIndicator={false} showPane={false} />

          {columns.map((column) => (
            <Column
              key={`${column.dataField}-${column.name || ""}`}
              {...column}
            />
          ))}
        </DataGrid>
      </div>
    </div>
  );
};

export default React.memo(Datagrid);
